import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Send, CheckCircle2, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API, api } from "@/lib/api";

export default function PublicSurveyPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null, done: false });
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/survey/${token}`).then(r => {
      setState({ loading: false, error: null, data: r.data, done: r.data.already_completed });
      if (r.data.already_completed) {
        const map = {}; (r.data.responses || []).forEach(x => map[x.question_id] = x.score);
        setAnswers(map); setComments(r.data.comments || "");
      }
    }).catch(e => setState({ loading: false, error: e.response?.data?.detail || "Invalid link", data: null, done: false }));
  }, [token]);

  const submit = async () => {
    const questions = state.data?.template?.questions || [];
    if (questions.some(q => !answers[q.id])) { alert("Please rate every question."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/public/survey/${token}`, {
        responses: Object.entries(answers).map(([question_id, score]) => ({ question_id, score })),
        comments,
      });
      setState(s => ({ ...s, done: true, result: r.data }));
    } catch (e) { alert(e.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-sky-50 to-purple-50"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  if (state.error) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"><div className="max-w-md w-full rounded-2xl bg-white shadow p-8 text-center"><div className="text-rose-600 font-semibold">{state.error}</div><p className="text-xs text-slate-500 mt-2">Please contact the sender if you believe this is a mistake.</p></div></div>;

  const { data } = state;
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-sky-50 to-purple-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-6 sm:p-8 border-b bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <div className="flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 opacity-80" /><div className="text-xs uppercase tracking-widest opacity-80">Feedback for</div></div>
            <div className="text-2xl font-extrabold">{data.client_name}</div>
            <p className="text-sm opacity-90 mt-2">Hi {data.contact_person || "there"}, this quick survey takes under a minute and helps us serve you better.</p>
          </div>
          {state.done ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <div className="text-xl font-extrabold mb-1">Thank you!</div>
              <p className="text-sm text-slate-600">Your feedback has been recorded. {data.client_name} will review it and follow up if needed.</p>
            </div>
          ) : (
            <div className="p-6 sm:p-8 space-y-5">
              {(data.template.questions || []).map(q => (
                <div key={q.id}>
                  <div className="text-sm font-semibold mb-2">{q.label}</div>
                  <div className="flex justify-between gap-1" data-testid={`survey-q-${q.id}`}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setAnswers(a => ({ ...a, [q.id]: n }))}
                        className={`flex-1 py-3 rounded-lg border transition-all ${answers[q.id] === n ? "bg-indigo-600 text-white border-indigo-600 shadow-md scale-[1.03]" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}
                        data-testid={`survey-score-${q.id}-${n}`}
                      >
                        <Star className={`h-4 w-4 mx-auto mb-0.5 ${answers[q.id] === n ? "fill-current" : ""}`} />
                        <div className="text-xs font-semibold">{n}</div>
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>Very poor</span><span>Excellent</span></div>
                </div>
              ))}
              <div>
                <div className="text-sm font-semibold mb-1">Additional comments <span className="text-slate-400 font-normal">(optional)</span></div>
                <Textarea data-testid="survey-comments" rows={3} value={comments} onChange={e => setComments(e.target.value)} placeholder="Tell us anything else you'd like us to know..." />
              </div>
              <Button data-testid="survey-submit" onClick={submit} disabled={busy} className="w-full h-12 brand-gradient text-white text-base shadow-lg">
                <Send className="h-4 w-4 mr-2" /> {busy ? "Submitting..." : "Submit feedback"}
              </Button>
              <p className="text-[11px] text-slate-400 text-center">Powered by ISOQuest · Your responses go directly to {data.client_name}. We never ask for passwords or payment details here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
